import { Link, useForm } from "@inertiajs/react";
import { Mail, ExternalLink } from "lucide-react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import SetupSummaryCard from "@/components/schools/setup/SetupSummaryCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import { SETUP_FRESH, SETUP_IN_PROGRESS } from "@/help/fixtures";

function SignupFormPreview() {
  const { data, setData } = useForm({
    school_name: "Lincoln Elementary",
    city: "Toronto",
    email: "principal@lincoln.ca",
    notes: "Annual spring fundraiser, ~200 students",
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign up your school</CardTitle>
        <CardDescription>Start a free trial: create your school's account and set up your read-a-thon right away.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField name="school_name" label="School name" required value={data.school_name} onChange={(e) => setData("school_name", e.target.value)} />
            <FormField name="city" label="City" required value={data.city} onChange={(e) => setData("city", e.target.value)} />
          </div>
          <FormField name="email" label="Your email" type="email" required value={data.email} onChange={(e) => setData("email", e.target.value)} />
          <FormField name="notes" label="Tell us about your read-a-thon" value={data.notes} onChange={(e) => setData("notes", e.target.value)} />
          <Button>Sign up your school</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function GettingStartedSchoolAdmin() {
  return (
    <>
      <p>
        Welcome. Here's how to get your school onto PagePledge for the first time. You can sign up and start a free trial straight away, and the setup guide walks you through the rest.
      </p>

      <h2>1. Sign up your school</h2>
      <p>
        Fill in the <Link href="/signup">school signup form</Link> with your school's details and the name and email of your main contact.
      </p>
      <Screenshot caption="The signup form" browser="pagepledge.com/signup">
        <div className="max-w-md mx-auto">
          <SignupFormPreview />
        </div>
      </Screenshot>

      <h2>2. Click the link in your email</h2>
      <p>
        As soon as you sign up, we'll send you a welcome email, subject "Welcome to PagePledge", with your sign-in link. The link works for 24 hours, and there's no password to make. Your account starts as a free trial.
      </p>
      <Screenshot caption="The welcome email">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="h-4 w-4" />
                <strong className="text-foreground">PagePledge</strong>
                <span>&lt;hello@pagepledge.com&gt;</span>
              </div>
              <p className="font-display text-base font-semibold text-foreground">Welcome to PagePledge</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Lincoln Elementary is set up on PagePledge and your free trial has started. Click below to sign in and start setting up.
              </p>
              <Button>Sign in to PagePledge <ExternalLink className="h-3 w-3" /></Button>
              <p className="text-xs text-muted-foreground italic">Link works for 24 hours.</p>
            </CardContent>
          </Card>
        </div>
      </Screenshot>
      <Callout variant="note">
        <p>If the link has expired, go back to the sign-in page and ask for another one with the same email. There are no passwords on PagePledge, only email links.</p>
        <p>The welcome email also has a link for sending us a feature request. If PagePledge doesn't do something you need, tell us.</p>
      </Callout>

      <h2>3. Follow the setup guide</h2>
      <p>
        After signing in, you'll find the <strong>setup guide</strong> waiting on your dashboard. It walks you through everything between now and launch day, one step at a time, in the order that works best. Each step says why it matters, what to do, and when to aim to have it done.
      </p>
      <Screenshot caption="Your dashboard on day one">
        <SetupSummaryCard setup={SETUP_FRESH} />
      </Screenshot>
      <p>
        Steps are marked <strong>Required to launch</strong>, <strong>Recommended</strong> or <strong>Optional</strong>. Only the required ones stop your campaign from going live. The recommended ones are there because they make the read-a-thon go better, but you can skip any that don't fit your school. Some steps open only once an earlier one is done: you can't invite teachers until there are classrooms to put them in. Steps with a padlock and <strong>Locked until paid</strong> open once your campaign fee is paid and we've approved the campaign.
      </p>

      <h2>4. The steps, in order</h2>
      <p><strong>Foundations</strong></p>
      <ul>
        <li><strong><Link href="/help/articles/school-settings">Check your school details</Link></strong> — your school's name as families know it, and your timezone. The timezone decides when each campaign day starts, so it comes before dates.</li>
        <li><strong><Link href="/help/articles/creating-a-campaign">Plan your read-a-thon</Link></strong> — dates, what you're raising money for, and goals. We suggest dates with a proper runway, a name to match the season, and a reading goal worked out from your number of students.</li>
        <li><strong><Link href="/help/articles/paying-for-your-campaign">Pay the campaign fee</Link></strong>. The free trial covers setting up; running the campaign costs a flat fee. Choose your plan, send the e-Transfer, and we'll approve the campaign. Connecting Square, inviting teachers and launching open up once it's approved.</li>
        <li><strong><Link href="/help/articles/connecting-square">Connect Square</Link></strong> so donations have somewhere to land. A new Square account has to verify its bank account first, so connect it as soon as your campaign is approved.</li>
      </ul>
      <p><strong>People</strong></p>
      <ul>
        <li><strong><Link href="/help/articles/importing-roster-csv">Add your students</Link></strong> — upload a class list and we'll create the classrooms, the students and everyone's activation codes.</li>
        <li><strong><Link href="/help/articles/inviting-teachers">Invite your teachers</Link></strong> and add them to their classrooms. The guide shows which classrooms still need one.</li>
        <li><strong><Link href="/help/articles/managing-members">Add a co-organizer</Link></strong> (optional) — a parent council member, your treasurer, or a lead teacher. On the free trial we don't email them an invitation; they sign in from the sign-in page with the email you entered.</li>
      </ul>
      <p><strong>Rewards</strong></p>
      <ul>
        <li><strong><Link href="/help/articles/setting-school-milestones">School-wide rewards</Link></strong> — we suggest a set sized to your goal, which you can change before adding. They're printed on the info and take-home sheets, so they come before those.</li>
        <li><strong><Link href="/help/articles/setting-student-prizes">Student prizes</Link></strong> (optional) — prizes each student earns on their own, plus top-student awards and prize draws.</li>
      </ul>
      <p><strong>Spread the word</strong></p>
      <ul>
        <li><strong>Brief your teachers</strong> — a plain-language overview of the read-a-thon and what you need from them, with your dates and prizes. Download it, then email or print it for your teachers.</li>
        <li><strong>Tell families what's coming</strong> — what a read-a-thon is, what happens next, your dates and prizes. It goes home the week before the take-home sheets.</li>
        <li>Both sheets open with <strong>Customize &amp; download</strong>. We've written everything for you: edit any of it, turn off what you don't need, and add a section of your own. The preview shows the actual page, a sheet never runs past one piece of paper, and there's a black-and-white mode for school printers. When it looks right, <strong>Download PDF</strong>.</li>
        <li><strong><Link href="/help/articles/printing-take-home-sheets">Print take-home sheets</Link></strong> — one per student, with the QR code their family scans to join. Aim to have them go home about two weeks before reading starts, once your campaign is approved: families can't activate before then.</li>
        <li><strong>Choose who gets which emails</strong> (optional) — PagePledge emails admins, teachers and families when reading starts, with daily or weekly progress, as it wraps up, and with the final results. They're all on to start with; switch off any your school would rather send itself. You can change this later under <strong>Settings</strong>.</li>
      </ul>
      <p><strong>Launch</strong></p>
      <ul>
        <li>The last step is a pre-flight check of everything above. There's no button to press: the campaign goes live by itself early on your start date, as long as it's paid for and approved, Square is connected and your students are in. It also has your school's donation page link, which opens when donations do, along with each student's page.</li>
      </ul>

      <Screenshot caption="Partway through setup">
        <SetupSummaryCard setup={SETUP_IN_PROGRESS} />
      </Screenshot>

      <Callout variant="tip" title="You don't have to finish in one sitting">
        <p>
          Your progress is saved as you go. Until launch, a banner at the top of every page shows how far along you are and takes you back to the next step. On the free trial it also shows your campaign fee, with a <strong>How to pay</strong> link.
        </p>
      </Callout>
    </>
  );
}
